import SearchIcon from "@mui/icons-material/Search";
import Flex from "@/components/Flex";
import SearchInput from "@/components/SearchInput";
import useDisclosure from "@/hooks/use-disclosure";
import {
  MANAGE_PATIENTS_LABEL,
  MANAGE_PATIENTS_SEARCH_WIDTH,
  PATIENT_ROW_ACTION,
} from "../group-visit-constant";
import { GroupPatient, PatientRowAction } from "../group-visit-types";
import { UseGroupPatients } from "../hooks/use-group-patients";
import GroupPatientTable from "./GroupPatientTable";
import RemovePatientSessionsModal from "./RemovePatientSessionsModal";

export type ManagePatientsTabProps = {
  roster: UseGroupPatients;
  onRowAction: (action: PatientRowAction, patient: GroupPatient) => void;
  canEdit?: boolean;
};

/**
 * Group roster with search and row actions. Removal is confirmed and handled
 * here; every other row action bubbles up.
 *
 * @author Rajkumar Rathod
 */
const ManagePatientsTab = ({
  roster,
  onRowAction,
  canEdit = true,
}: ManagePatientsTabProps) => {
  const {
    patients,
    totalCount,
    isLoading,
    searchKey,
    setSearchKey,
    pagination,
    setPagination,
    selection,
    setSelection,
    removePatient,
    isRemoving,
  } = roster;

  const removeDisclosure = useDisclosure();

  const handleRowAction = (action: PatientRowAction, patient: GroupPatient) => {
    if (action === PATIENT_ROW_ACTION.REMOVE) {
      removeDisclosure.open(patient);
      return;
    }
    onRowAction(action, patient);
  };

  const handleRemoveConfirm = async (cancelUpcomingSessions: boolean) => {
    const patient: GroupPatient | undefined = removeDisclosure.data;
    if (!patient) return;

    try {
      await removePatient(patient, cancelUpcomingSessions);
      removeDisclosure.close();
    } catch {
      // The hook already surfaced the error; keep the dialog open to retry.
    }
  };

  return (
    <Flex flexDirection="column" gap={1.5} sx={{ px: 0.5, pb: 1 }}>
      <SearchInput
        searchKey={searchKey}
        onSearch={setSearchKey}
        onClear={() => setSearchKey("")}
        placeholder={MANAGE_PATIENTS_LABEL.searchPlaceholder}
        name="patientSearch"
        startIcon={<SearchIcon sx={{ fontSize: 16 }} />}
        sx={{ width: MANAGE_PATIENTS_SEARCH_WIDTH, maxWidth: "100%" }}
      />

      <GroupPatientTable
        patients={patients}
        isLoading={isLoading}
        totalCount={totalCount}
        pagination={pagination}
        onPaginationChange={setPagination}
        selection={selection}
        onSelectionChange={setSelection}
        searchKey={searchKey}
        canEdit={canEdit}
        onRowAction={handleRowAction}
      />

      <RemovePatientSessionsModal
        open={removeDisclosure.opened}
        onClose={removeDisclosure.close}
        onConfirm={handleRemoveConfirm}
        isSubmitting={isRemoving}
      />
    </Flex>
  );
};

export default ManagePatientsTab;
